import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { MoreHoriz } from '@mui/icons-material';
// Outline icons, to match the sidebar nav this menu opens out of -- those go
// through `DynamicIcon`/`iconsMap`, which resolves to these same lucide glyphs
// (`profile` -> `User`, `logout` -> `LogOut`). `@mui/icons-material`'s defaults
// are the filled variants, so the menu read as a different icon set to
// everything around it. `size={20}` matches `SidebarNav`'s `width={20}`.
import { FileText, LogOut, Moon, Shield, Sun, User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { updateNotificationPreferencesThunk } from 'redux/slices/miscSlice';
import { useHistory } from 'react-router-dom';
import { checkSession, logoutUser, resetUIState } from 'redux/slices/authSlice';
import useResetOnTourStep from '@/features/productTours/useResetOnTourStep';
import { useThemeContext } from 'contexts/ThemeContext';

const ProfileCircularMenu = ({
  userInfo,
  userDetails,
  width,
  height,
  noPointerEvents,
  dataTour,
  variant = 'avatar',
  collapsed = false,
  trailingAction,
  showThemeToggle = false,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { themeToggler } = useThemeContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const avatarButtonRef = useRef(null);

  // Product-tour support (only for the instance that declares a `dataTour`
  // base name, i.e. the topbar one): when the active tour's current step
  // targets this menu's popper, force it open — the menu's open state lives
  // in this component, so a tour step can't reach it otherwise (mirrors the
  // `forceOpenKey` pattern in Dashboard.js). The reset hook below closes it
  // again once the tour steps past the avatar/menu or stops.
  const avatarTourTarget = dataTour ? `[data-tour="${dataTour}-avatar"]` : null;
  const menuTourTarget = dataTour ? `[data-tour="${dataTour}-menu"]` : null;
  // The step selector comes from the store rather than a `toursRegistry`
  // lookup: this menu sits in the sidebar, so it is in the initial bundle, and
  // importing the registry here put all of the tour prose there with it. The
  // lazy tour runner publishes it -- see `currentStepTarget` in
  // `productTourSlice`.
  const { isRunning, currentStepTarget } = useSelector(
    (state) => state.productTour,
  );

  useEffect(() => {
    if (!menuTourTarget || !isRunning) return;
    if (currentStepTarget === menuTourTarget) {
      setAnchorEl((el) => el || avatarButtonRef.current);
    }
  }, [menuTourTarget, isRunning, currentStepTarget]);

  useResetOnTourStep(dataTour ? [avatarTourTarget, menuTourTarget] : [], () =>
    setAnchorEl(null),
  );

  const handleProfileMenuClick = (event) => {
    if (anchorEl) setAnchorEl(null);
    // The row's own element rather than `event.currentTarget`: in the sidebar
    // the name and the dots are separate buttons, and anchoring to whichever
    // one was hit would make the menu jump between clicks.
    else setAnchorEl(avatarButtonRef.current || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    if (localStorage.getItem('notificationToken')) {
      try {
        await dispatch(
          updateNotificationPreferencesThunk({ pushnotification: false }),
        ).unwrap();
        localStorage.removeItem('notificationToken');
      } catch (_e) {
        /* noop */
      }
    }
    dispatch(logoutUser());
    dispatch(
      resetUIState(() => {
        dispatch(checkSession());
      }),
    );
  };

  const isSidebar = variant === 'sidebar';

  const fullName = [userDetails?.firstName, userDetails?.lastName]
    .filter(Boolean)
    .join(' ');

  const avatar = (
    <Avatar
      alt={userDetails?.firstName?.toUpperCase()}
      src={userDetails?.profilePicture?.url}
      sx={{
        width: width || 30,
        height: height || 30,
        border: '1px solid var(--dc-border)',
      }}
    >
      {userDetails?.firstName?.[0]?.toUpperCase()}
    </Avatar>
  );

  const handleTriggerClick = (e) => {
    if (noPointerEvents) {
      history.push('/profile');
    } else {
      handleProfileMenuClick(e);
    }
  };

  return (
    <Box sx={isSidebar ? { width: 1 } : undefined}>
      {isSidebar ? (
        /*
          The row carries two triggers for one menu -- the name and the dots --
          plus whatever `trailingAction` the sidebar hangs here. They are
          siblings rather than one big button because a button cannot legally
          contain another one, and the trailing action has to stay separately
          clickable. Collapsed, the rail has no width for a row, so the same
          pieces stack and the dots drop out: the avatar alone opens the menu.
        */
        <Box
          ref={avatarButtonRef}
          sx={{
            width: 1,
            display: 'flex',
            flexDirection: collapsed ? 'column' : 'row',
            alignItems: 'center',
            gap: collapsed ? 0.25 : 0.5,
          }}
        >
          {collapsed ? trailingAction : null}
          <Tooltip
            title={collapsed ? fullName || userInfo?.email || '' : ''}
            arrow
            placement="right"
          >
            <ButtonBase
              data-trackid={'open_profile_menu'}
              data-tour={avatarTourTarget ? `${dataTour}-avatar` : undefined}
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleTriggerClick}
              sx={{
                width: collapsed ? 1 : 'auto',
                flexGrow: 1,
                minWidth: 0,
                borderRadius: 2,
                padding: collapsed ? '8px 4px' : '8px',
                gap: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                textAlign: 'left',
                ':hover': { backgroundColor: 'custom.primarySubtle' },
              }}
            >
              {avatar}
              {!collapsed ? (
                <Typography
                  variant="subtitle2"
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    color: 'text.primary',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {fullName || userInfo?.email}
                </Typography>
              ) : null}
            </ButtonBase>
          </Tooltip>
          {!collapsed ? (
            <>
              {trailingAction}
              <IconButton
                size="small"
                data-trackid={'open_profile_menu'}
                aria-label="Account menu"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleTriggerClick}
                sx={{ flexShrink: 0, color: 'text.secondary' }}
              >
                <MoreHoriz sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          ) : null}
        </Box>
      ) : (
        <Button
          ref={avatarButtonRef}
          data-trackid={'open_profile_menu'}
          data-tour={avatarTourTarget ? `${dataTour}-avatar` : undefined}
          aria-controls="profile-menu"
          aria-haspopup="true"
          onClick={handleTriggerClick}
          sx={{
            padding: width & height ? 0 : '6px 8px',
          }}
        >
          {avatar}
        </Button>
      )}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        keepMounted
        MenuListProps={
          menuTourTarget ? { 'data-tour': `${dataTour}-menu` } : undefined
        }
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        // The sidebar trigger sits on the floor of the viewport, so its menu
        // has to grow upward and out over the content instead of downward.
        transformOrigin={
          isSidebar
            ? { horizontal: 'left', vertical: 'bottom' }
            : { horizontal: 'left', vertical: 'top' }
        }
        anchorOrigin={
          isSidebar
            ? { horizontal: 'right', vertical: 'top' }
            : { horizontal: 'left', vertical: 'bottom' }
        }
        style={isSidebar ? undefined : { marginTop: '15px' }}
      >
        <MenuItem
          data-trackid={'profile_menu_home_option'}
          style={{}}
          onClick={() => {
            // handleTrackEvent(e, 'profile_menu_home_option');
            history.push('/profile');
            handleClose();
          }}
        >
          <User size={20} style={{ marginRight: '10px' }} />
          Profile Settings
        </MenuItem>
        {/*
          The theme switch used to be a row in the sidebar's own footer. It
          lives here instead so the sidebar floor is just the account row, and
          because this is where someone looks for a personal setting. Closing
          on click is deliberate: the change is visible behind the menu.
        */}
        {showThemeToggle ? (
          <MenuItem
            data-trackid={'theme_toggler'}
            onClick={() => {
              themeToggler();
              handleClose();
            }}
          >
            {theme.palette.mode === 'light' ? (
              <Moon size={20} style={{ marginRight: '10px' }} />
            ) : (
              <Sun size={20} style={{ marginRight: '10px' }} />
            )}
            {theme.palette.mode === 'light' ? 'Dark mode' : 'Light mode'}
          </MenuItem>
        ) : null}
        <MenuItem
          data-trackid={'profile_menu_privacy_option'}
          onClick={() => {
            history.push('/privacy');
            handleClose();
          }}
        >
          <Shield size={20} style={{ marginRight: '10px' }} />
          Privacy Policy
        </MenuItem>
        <MenuItem
          data-trackid={'profile_menu_tos_option'}
          onClick={() => {
            history.push('/tos');
            handleClose();
          }}
        >
          <FileText size={20} style={{ marginRight: '10px' }} />
          Terms of Service
        </MenuItem>
        <Divider />
        <MenuItem
          data-trackid={'profile_menu_logout_option'}
          style={{}}
          onClick={() => {
            // handleTrackEvent(e, 'profile_menu_logout_option');
            handleLogoutClick();
          }}
        >
          <LogOut size={20} style={{ marginRight: '10px' }} />
          Logout
        </MenuItem>
        {/*
          `text.secondary`, not the hardcoded `rgba(0, 0, 0, 0.54)` this
          replaces: that literal is a near-black, so on the dark theme's paper
          the address was invisible rather than merely de-emphasised.

          Not `text.disabled` either, despite the row being inert -- that token
          is deliberately low-contrast (2.06:1 in light) because disabled text
          is exempt under WCAG 1.4.3, and this is readable information, not a
          disabled control. `text.secondary` keeps the original intent and
          clears AA in both modes: 5.4:1 light, 9.6:1 dark.
        */}
        <MenuItem
          sx={{
            color: 'text.secondary',
            pointerEvents: 'none',
          }}
        >
          {userInfo?.email}
        </MenuItem>
      </Menu>
    </Box>
  );
};

ProfileCircularMenu.propTypes = {
  userInfo: PropTypes.object,
  userDetails: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number,
  noPointerEvents: PropTypes.bool,
  dataTour: PropTypes.string,
  variant: PropTypes.oneOf(['avatar', 'sidebar']),
  collapsed: PropTypes.bool,
  /** An extra control for the sidebar row, rendered left of the dots. */
  trailingAction: PropTypes.node,
  /** Dark/light is dev-only app-wide, so the caller decides. */
  showThemeToggle: PropTypes.bool,
};

export default ProfileCircularMenu;
